import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"

/**
 * Three contexts where the identity-mismatch shell renders. Discriminator
 * value ships on the wire as `data-mismatch-context` (UX Spec §12.1) so QA
 * Playwright + Vitest can fork copy assertions on a single testid.
 *
 * - `anon`        — logged-out OKS-21 original: TikTok session present, no
 *                   OKS session, candidate handle ≠ TikTok handle.
 * - `recognised`  — OKS-24 Flow E: 2nd-brand landing, identity check fails
 *                   BEFORE any membership mutation.
 * - `post_oauth`  — OKS-24 Flow E': 1st-brand wizard step 1, OAuth callback
 *                   handle ≠ candidate (server quarantines membership +
 *                   redirects here).
 */
export type IdentityMismatchContext = "anon" | "recognised" | "post_oauth"

export interface IdentityMismatchViewProps {
  /** Discriminates which copy + CTA shape renders. */
  context: IdentityMismatchContext
  /**
   * Handle to surface in the body copy. For `anon` + `post_oauth` this is
   * the TikTok-returned handle (what the user signed in to TikTok as). For
   * `recognised` it's the user's verified TikTok handle on OKS.
   */
  handle: string
  /**
   * Server-resolved CTA target. For `anon` it's a `/sign_in`-style link;
   * for `recognised` + `post_oauth` it's `mailto:` resolved server-side
   * from `brand.support_email.presence || credentials.support.email`. The
   * server is the single decision point; FE only renders.
   *
   * `null` is treated as a server contract violation: the CTA is omitted
   * (heading + body still render). Better to fail visibly than to silently
   * mis-direct (UX review round 2 — the previous `signInPath()` fallback
   * on null is dropped).
   */
  cta_url: string | null
  /**
   * Brand name for the contact-CTA copy (e.g. "Contact your brand"
   * resolves to `mailto:` with subject prefilled). Used for accessibility
   * announcements + visible chrome — null on `anon` (OKS-21 path doesn't
   * carry brand info on the public landing variant).
   */
  brand_name?: string | null
  className?: string
}

/**
 * IdentityMismatchView — shared presentation rendered by:
 *   1. `pages/Onboarding/IdentityMismatch.tsx` (first-time TikTok mismatch
 *      dead-end after OAuth callback — Flow E', `context="post_oauth"`)
 *   2. `pages/Public/InviteLanding.tsx` when `state="identity_mismatch"`
 *      (second-brand mismatch BEFORE mutation — Flow E,
 *      `context="recognised"`); it can also render the OKS-21 anonymous
 *      case (`context="anon"`).
 *
 * Single component, multiple Inertia entry points → one source of truth
 * for the dead-end UX (FE TRD v2 §Components — F16 task).
 *
 * **No retry CTA** for `recognised` / `post_oauth` — these are terminal
 * dead-ends per UX Spec §12.1; the brand re-issues the invite. The `anon`
 * variant keeps the OKS-21 retry affordance ("Try a different account").
 *
 * **testid contract** (UX Spec §12.1 + FE TRD §testid):
 *   - root `invite-landing-error-mismatch` carries `data-mismatch-context`
 *   - dedicated-page wrapper (`pages/Onboarding/IdentityMismatch.tsx`)
 *     adds `identity-mismatch-page` ancestor + `tiktok-failure-identity-
 *     mismatch` testid carried over from OKS-23.
 */
export function IdentityMismatchView({
  context,
  handle,
  cta_url,
  brand_name,
  className,
}: IdentityMismatchViewProps) {
  const { t } = useTranslation()
  const heading = t(`wizard.identity_mismatch.${context}.heading`)
  const body = t(`wizard.identity_mismatch.${context}.body`, { handle })
  const ctaLabel = t(`wizard.identity_mismatch.${context}.cta`)

  return (
    <div
      data-testid="invite-landing-error-mismatch"
      data-mismatch-context={context}
      className={["flex flex-col gap-4", className ?? ""].join(" ")}
      // No `role="status"` — this is a terminal dead-end page, not a
      // live-region update. Announcing it via aria-live would interrupt
      // SR users with an unexpected "polite" announcement on a
      // server-rendered static surface (UX review round 2).
    >
      <h2 className="text-foreground text-xl font-semibold">{heading}</h2>
      <p className="text-muted-foreground text-base leading-relaxed">{body}</p>
      {cta_url && (
        <Button
          asChild
          variant={context === "anon" ? "outline" : "default"}
          size="lg"
          // For `anon` context (OKS-21 retry shape) the canonical testid is
          // `invite-landing-cta` — kept stable for OKS-21 Playwright contracts.
          // For `recognised` / `post_oauth` (OKS-24 dead-ends) we add the
          // `identity-mismatch-support-link` testid so QA can address the
          // mailto support CTA semantically.
          data-testid={
            context === "anon"
              ? "invite-landing-cta"
              : "identity-mismatch-support-link"
          }
          className="w-full sm:w-auto"
        >
          <a
            href={cta_url}
            // mailto: links should NOT open in a new tab (would create a blank
            // tab when no mail client is registered). Only set `rel` for the
            // anon variant where cta_url is an HTTP URL.
            {...(context === "anon" ? { rel: "noopener noreferrer" } : {})}
            aria-label={
              brand_name && context !== "anon"
                ? `${ctaLabel}: ${brand_name}`
                : undefined
            }
          >
            {ctaLabel}
          </a>
        </Button>
      )}
    </div>
  )
}

export default IdentityMismatchView
